/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import 'intersection-observer';
import { animateOptions } from '../options/animate.options';

/**
 * Animate directive
 *
 * A flexible directive for adding CSS classes to elements when they enter the viewport.
 * Supports multiple targets, staggered animations, and configurable options.
 *
 * @example
 *
 * ```html
 * <!-- Basic usage - animate the element itself -->
 * <div amb-animate>Content</div>
 *
 * <!-- With preset -->
 * <div amb-animate="carousel">...</div>
 * <div amb-animate="narrative">...</div>
 *
 * <!-- With custom targets -->
 * <div amb-animate data-targets=".item">
 *   <div class="item">Item 1</div>
 *   <div class="item">Item 2</div>
 * </div>
 * ```
 */
export const animateDirective = {
  /**
   * Animate options container
   */
  options: animateOptions,

  /**
   * A map to hold all observer instances for each element
   *
   * @type {Map<string, {observers: IntersectionObserver[], options: object}>}
   */
  instances: new Map(),

  /**
   * Initialize animate directive for the given element
   *
   * @param {HTMLElement} element HTML DOM element
   * @param {object}      options Animate options
   */
  init(element, options = {}) {
    const {
      targets = 'self',
      class: animateClass = 'in-view',
      threshold = 0.1,
      once = true,
      stagger = 0,
      rootMargin = '0px'
    } = options;

    // Get target elements
    const targetElements = this.getTargetElements(element, targets);

    if (!targetElements.length) {
      return;
    }

    const observers = [];
    const observerOptions = {
      threshold,
      rootMargin
    };

    // Create observers for each target
    targetElements.forEach((target, index) => {
      const delay = stagger * index;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (delay > 0) {
              setTimeout(() => {
                target.classList.add(animateClass);
              }, delay);
            } else {
              target.classList.add(animateClass);
            }

            if (once) {
              observer.disconnect();
            }
          } else if (!once) {
            target.classList.remove(animateClass);
          }
        });
      }, observerOptions);

      observer.observe(target);
      observers.push(observer);
    });

    // Store instance for cleanup
    this.instances.set(element.ambid, { observers, options });
  },

  /**
   * Destroy animate directive for the given element
   *
   * @param {HTMLElement} element HTML DOM element
   */
  destroy(element) {
    const ambid = element.ambid;

    if (this.instances.has(ambid)) {
      const { observers } = this.instances.get(ambid);

      observers.forEach((observer) => observer.disconnect());
      this.instances.delete(ambid);
    }
  },

  /**
   * Get target elements based on selector
   *
   * @private
   *
   * @param {HTMLElement} element  The directive element
   * @param {string}      selector Target selector
   *
   * @returns {HTMLElement[]} Array of target elements
   */
  getTargetElements(element, selector) {
    if (!selector || selector === 'self') {
      return [element];
    }

    // Handle :scope selector for direct children
    if (selector.startsWith(':scope')) {
      return Array.from(element.querySelectorAll(selector));
    }

    // Find elements matching selector within the element
    const targets = element.querySelectorAll(selector);

    return targets.length ? Array.from(targets) : [element];
  }
};
